import type {
    BuildingInfoSource,
    BuildingInfoSourceId,
    BuildingInfoSourceInput,
} from './models';

const SOURCE_CONTEXTS: Record<
    BuildingInfoSourceId,
    BuildingInfoSource['context']
> = {
    geoscape: 'primary',
    user: 'caution',
};

const UNKNOWN_VALUE = 'Unknown';

export function buildingFloorAreaValue(
    building: BuildingInfoSourceInput
): string {
    if (hasNumberValue(building.floorArea)) {
        return formatNumber(building.floorArea, 'm²');
    }

    const fallbackValue = isSecondary(building)
        ? null
        : building.geoscapeProfile?.totalFloorArea;
    return formatNumber(fallbackValue, 'm²');
}

export function buildingLotsValue(building: BuildingInfoSourceInput): string {
    const userValue = building.lots?.trim() ?? '';

    if (userValue.length > 0) {
        return withSuffix(userValue, 'lots');
    }

    if (isSubPremise(building)) {
        return formatLots(1);
    }

    const fallbackValue = isSecondary(building)
        ? null
        : building.geoscapeProfile?.lotCount;
    return formatLots(fallbackValue);
}

export function buildingFloorAreaSources(
    building: BuildingInfoSourceInput
): readonly BuildingInfoSource[] {
    const userValue = building.floorArea;
    const geoscapeValue = building.geoscapeProfile?.totalFloorArea;
    const hasUserValue = hasNumberValue(userValue);
    const hasGeoscapeValue = hasNumberValue(geoscapeValue);

    if (!hasUserValue && !hasGeoscapeValue) {
        return [];
    }

    const userValueOverridesGeoscape =
        hasUserValue &&
        (isSecondary(building) ||
            !hasGeoscapeValue ||
            userValue !== geoscapeValue);
    const sources: BuildingInfoSource[] = [];

    if (userValueOverridesGeoscape) {
        sources.push(createUserSource(formatNumber(userValue, 'm²'), true));
    }

    if (hasGeoscapeValue) {
        sources.push(
            createGeoscapeSource(
                formatNumber(geoscapeValue, 'm²'),
                !isSecondary(building) && !userValueOverridesGeoscape,
                isSecondary(building)
            )
        );
    }

    return sources;
}

export function buildingLotSources(
    building: BuildingInfoSourceInput
): readonly BuildingInfoSource[] {
    if (isSecondary(building)) {
        return secondaryBuildingLotSources(building);
    }
    const userValue = building.lots?.trim() ?? '';
    const geoscapeValue = building.geoscapeProfile?.lotCount;
    const hasUserValue = userValue.length > 0;
    const hasGeoscapeValue = hasNumberValue(geoscapeValue);

    if (!hasUserValue && !hasGeoscapeValue) {
        return [];
    }

    const formattedUserValue = hasUserValue
        ? withSuffix(userValue, 'lots')
        : null;
    const formattedGeoscapeValue = hasGeoscapeValue
        ? formatLots(geoscapeValue)
        : null;
    const userValueOverridesGeoscape =
        formattedUserValue !== null &&
        (formattedGeoscapeValue === null ||
            formattedUserValue !== formattedGeoscapeValue);
    const sources: BuildingInfoSource[] = [];

    if (userValueOverridesGeoscape && formattedUserValue !== null) {
        sources.push(createUserSource(formattedUserValue, true));
    }

    if (formattedGeoscapeValue !== null) {
        sources.push(
            createGeoscapeSource(
                formattedGeoscapeValue,
                !isSecondary(building) && !userValueOverridesGeoscape,
                isSecondary(building)
            )
        );
    }

    return sources;
}

function secondaryBuildingLotSources(
    building: BuildingInfoSourceInput
): readonly BuildingInfoSource[] {
    const sources: BuildingInfoSource[] = [];
    const lots = building.lots?.trim() ?? '';
    if (isSubPremise(building) || lots) {
        const source = createUserSource(buildingLotsValue(building), true);
        sources.push({
            ...source,
            label:
                isSubPremise(building) && (!lots || lots === '1')
                    ? 'Unit default'
                    : 'User supplied',
        });
    }
    const count = building.geoscapeProfile?.lotCount;
    if (hasNumberValue(count))
        sources.push(createGeoscapeSource(formatLots(count), false, true));
    return sources;
}

function createUserSource(
    value: string,
    selected: boolean
): BuildingInfoSource {
    return {
        context: SOURCE_CONTEXTS.user,
        id: 'user',
        label: 'User supplied',
        selected,
        value,
    };
}

function createGeoscapeSource(
    value: string,
    selected: boolean,
    referenceOnly = false
): BuildingInfoSource {
    return {
        context: SOURCE_CONTEXTS.geoscape,
        id: 'geoscape',
        label: referenceOnly ? 'Geoscape — whole building' : 'Geoscape',
        selected,
        value,
    };
}

function formatNumber(value: number | null | undefined, unit: string): string {
    if (!hasNumberValue(value)) {
        return UNKNOWN_VALUE;
    }

    return `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}

function formatLots(value: number | null | undefined): string {
    if (!hasNumberValue(value)) {
        return UNKNOWN_VALUE;
    }

    return `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value)} ${value === 1 ? 'lot' : 'lots'}`;
}

function withSuffix(value: string, suffix: string): string {
    const trimmedValue = value.trim();

    if (trimmedValue.toLowerCase().endsWith(suffix.toLowerCase())) {
        return trimmedValue;
    }

    return `${trimmedValue} ${suffix === 'lots' && Number(trimmedValue) === 1 ? 'lot' : suffix}`;
}

function hasNumberValue(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function isSecondary(building: BuildingInfoSourceInput): boolean {
    return isSubPremise(building) || building.isSecondaryAddress === true;
}

function isSubPremise(building: BuildingInfoSourceInput): boolean {
    return building.subPremiseType != null;
}
